// The grid custom dashboards are drawn on (react-grid-layout). In edit mode widgets are dragged by
// their name bar and made wider or narrower from their right edge; widgets slide up to fill empty
// space, and whatever space is left shows "+ Add widget" slots. Each widget is as tall as its
// content. Phones get the widgets stacked in reading order.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { COLS, MARGIN, ROW, SLOT_H, SLOT_W, compact, freeSlots, readingOrder, rowsFor, sizeOfW } from './gridLayout';
import { useI18n } from './i18n';

// Reports the content height of a widget, so its box can fit it.
function Measured({ id, onSize, children }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const report = () => onSize(id, el.offsetHeight);
    report();
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [id, onSize]);
  return <div ref={ref} className="dw-measure">{children}</div>;
}

export default function DashGrid({ layout, editing, limits, renderItem, onCommit, onSlot, narrow }) {
  const { t } = useI18n();
  const wrap = useRef(null);
  const [width, setWidth] = useState(0);
  const [heights, setHeights] = useState({});
  useLayoutEffect(() => {
    const el = wrap.current;
    if (!el) return undefined;
    const set = () => setWidth(el.clientWidth);
    set();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(set) : null;
    if (ro) ro.observe(el);
    return () => ro && ro.disconnect();
  }, []);
  const onSize = useCallback((id, px) => setHeights((h) => (h[id] === px ? h : { ...h, [id]: px })), []);
  // Forget heights of widgets that are gone.
  useEffect(() => {
    setHeights((h) => {
      const keep = Object.fromEntries(Object.entries(h).filter(([id]) => layout.some((w) => w.id === id)));
      return Object.keys(keep).length === Object.keys(h).length ? h : keep;
    });
  }, [layout]);

  // Each widget's box: its own height if stretched, otherwise one that fits the content.
  const boxes = useMemo(() => layout.map((w) => ({ ...w, h: heights[w.id] ? rowsFor(heights[w.id]) : w.h })), [layout, heights]);
  const settled = useMemo(() => compact(boxes), [boxes]);
  const slots = useMemo(() => (editing ? freeSlots(settled) : []), [editing, settled]);

  if (narrow) {
    return <div className="dw-stack">{readingOrder(layout).map((w) => <div key={w.id} className="dw-stack-item">{renderItem(w)}</div>)}</div>;
  }

  const colW = width ? (width - MARGIN * (COLS - 1)) / COLS : 0;
  const px = (n, unit) => n * unit + (n - 1) * MARGIN;
  const bottom = Math.max(settled.reduce((m, w) => Math.max(m, w.y + w.h), 0), ...slots.map((s) => s.y + SLOT_H));
  const rglLayout = boxes.map((w) => {
    const [minW, maxW] = limits(w.id);
    return { i: w.id, x: w.x, y: w.y, w: w.w, h: w.h, minW, maxW, minH: 3 };
  });
  // Drags and resizes: new positions and widths (heights always follow the content).
  const commit = (next) => onCommit(layout.map((w) => {
    const n = next.find((x) => x.i === w.id);
    if (!n) return w;
    const { hLocked, ...rest } = w;
    return { ...rest, x: n.x, y: n.y, w: n.w, size: sizeOfW(n.w) };
  }));

  return (
    <div className={`dw-rgl${editing ? ' editing' : ''}`}>
    <div ref={wrap} className="dw-rgl-inner" style={{ minHeight: editing ? bottom * (ROW + MARGIN) : undefined }}>
      {editing && width > 0 && (
        <div className="dw-slots" aria-hidden={false}>
          {slots.map((s) => (
            <button key={`${s.x}-${s.y}`} type="button" className="dw-slot" onClick={() => onSlot(s)}
              style={{ left: s.x * (colW + MARGIN), top: s.y * (ROW + MARGIN), width: px(SLOT_W, colW), height: px(SLOT_H, ROW) }}>
              <span aria-hidden="true">+</span>{t('Add widget')}
            </button>
          ))}
        </div>
      )}
      {width > 0 && (
        <GridLayout className="dw-rgl-grid" width={width} cols={COLS} rowHeight={ROW} margin={[MARGIN, MARGIN]} containerPadding={[0, 0]}
          layout={rglLayout} compactType="vertical" isDraggable={editing} isResizable={editing} resizeHandles={['e']}
          draggableHandle=".dw-name" draggableCancel="button, select, input"
          onDragStop={(next) => commit(next)} onResizeStop={(next) => commit(next)}>
          {boxes.map((w) => (
            <div key={w.id} className="dw-cell">
              <Measured id={w.id} onSize={onSize}>{renderItem(w)}</Measured>
            </div>
          ))}
        </GridLayout>
      )}
    </div>
    </div>
  );
}
